/**
 * 組織レポート（実データ版） — 会社全メンバーの gb74 個人スコアを集計して表示する。
 *
 * analysisApi.getCompanyResults() で会社の全分析結果を取得し、
 * buildOrgReportFromResults() で OrgReportData を構築して Gb74OrgReport に渡す。
 * スコア（働きがい/つながり/離職/総合）は確定集約ロジック由来の実データ。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Alert, CircularProgress, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Gb74OrgReport } from './Gb74OrgReport';
import { analysisApi } from '../api/analysisApi';
import { unwrapGb74Scores } from '../utils/gb74Report';
import {
  buildOrgReportFromResults,
  type OrgMemberResult,
} from '../utils/buildOrgReportFromResults';
import { classifyOrgType } from '../utils/orgTypeClassifier';
import { findPatternByCode } from '../../admin/api/teamJudgmentPatternApi';
import type { OrgReportData } from '../utils/orgReportDummy';

export const OrgReportRealPage: React.FC = () => {
  const [data, setData] = useState<OrgReportData | null>(null);
  const [memberCount, setMemberCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await analysisApi.getCompanyResults();
      // gb74 形式の結果のみ抽出し、部署名付きで OrgMemberResult に変換
      const members: OrgMemberResult[] = [];
      for (const r of res.results ?? []) {
        const scores = unwrapGb74Scores((r as { scores?: unknown }).scores);
        if (!scores) continue;
        members.push({
          scores,
          departmentName: (r as { departmentName?: string | null }).departmentName ?? null,
        });
      }

      if (members.length === 0) {
        setData(null);
        setMemberCount(0);
      } else {
        // 集約A/B/C/Dで組織タイプを判定し、該当パターン(tdesc)をDBから取得して結線する。
        // 取得失敗時は teamPattern=null → 従来テンプレのリード文にフォールバック。
        const orgType = classifyOrgType(members);
        const teamPattern = await findPatternByCode(orgType.code);
        // §03レベルB: 相談ネットワーク(匿名化済み)を取得。取得失敗/未整備なら従来の健全度ビューにフォールバック。
        let consultNetwork = undefined;
        try {
          const net = await analysisApi.getCompanyConsultNetwork();
          if (net.consultNetwork?.depts?.length) consultNetwork = net.consultNetwork;
        } catch (e) {
          console.warn('[OrgReportRealPage] consult-network 取得失敗、健全度ビューにフォールバック:', e);
        }
        const built = buildOrgReportFromResults(members, {
          companyName: '会社全体',
          meta: `${members.length}名の回答に基づく集計`,
          teamPattern,
          applyHerrmannLoad: true, // 組織の組み合わせ(コミュニケーション負荷)セクションを表示＋つながりスコア補正
        });
        setData({ ...built, consultNetwork });
        setMemberCount(members.length);
      }
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(
        status === 403
          ? '権限がありません（経営者向け / COMPANY_WIDE）'
          : '組織レポートの取得に失敗しました'
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', px: 2, py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {memberCount > 0 ? `会社全体 ${memberCount}名の回答を集計` : '組織レポート（実データ）'}
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={isLoading}>
          再取得
        </Button>
      </Box>

      {isLoading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!isLoading && error && <Alert severity="error">{error}</Alert>}

      {!isLoading && !error && !data && (
        <Alert severity="info">
          集計対象の回答（GB組織診断74）がまだありません。74問に回答した社員が増えると、ここに実データの組織レポートが表示されます。
        </Alert>
      )}

      {!isLoading && !error && data && <Gb74OrgReport data={data} />}
    </Box>
  );
};

export default OrgReportRealPage;
